import moment from "moment";
import { AppShape, HLHLContactRole } from "@eave/shared/types/api";

import { TRUS_POSTPONE_UW_KEY } from "@eave/shared/config/localStorageKeys";

import APIClient from "web_modules/APIClient";
import { TRUSEventShape } from "web_modules/Events/types";
import { LoanStatus } from "web_modules/shared/loan_statuses";

export const endpoint = appId => `/loan_applications/${appId}/hurdles/underwriting_summary`;

export const updateLoanStatus = (appId, newStatus, underwriting_summary) => {
  const { loan_decision, loan_details } = underwriting_summary;
  const payload = {
    loan_status: newStatus,
    loan_decision,
    loan_details,
  };

  return APIClient.put(endpoint(appId), payload);
};

export const savePromptDismissedEvent = (appId, hurdleKind) => {
  const note = "User has opted not to set Loan Status to In Borrower Underwriting.";

  return APIClient.post(`/uw/${appId}/events`, {
    appId,
    kind: hurdleKind,
    fields: {
      note,
      pinned: false,
    },
  }) as Promise<{ event: TRUSEventShape }>;
};

// Maps the loan's appID to a timestamp for when that loan app's "Begin Underwriting" prompt was dismissed
type DismissedAppsMap = Map<number, string>;

// Helpers for serializing to/from localStorage
export const serializeMap = (m: DismissedAppsMap) => JSON.stringify(Array.from(m.entries()));
export const deserializeMap = (s: string): DismissedAppsMap => new Map(JSON.parse(s));

/**
 * mapIfExists
 * @returns A mapping of loan application IDs to the timestamps at which the underwriting was postponed, OR null
 */
export const mapIfExists = () => {
  const dismissedAppsMapSerialized = localStorage.getItem(TRUS_POSTPONE_UW_KEY);

  // De-serialize
  if (dismissedAppsMapSerialized) {
    return deserializeMap(dismissedAppsMapSerialized);
  }

  return null;
};

export const removeTimestampForID = (dismissedAppsMap: DismissedAppsMap, id) => {
  if (dismissedAppsMap && dismissedAppsMap.has(id)) {
    dismissedAppsMap.delete(id);
    localStorage.setItem(TRUS_POSTPONE_UW_KEY, serializeMap(dismissedAppsMap));
  }
};

export const withinThirtyMinutes = (id): Boolean => {
  const dismissedAppsMap = mapIfExists();

  if (!dismissedAppsMap || !dismissedAppsMap.has(id)) {
    return false;
  }

  // Grab the value from the map
  const lastDismissedAtTimestamp = dismissedAppsMap.get(id);

  // Variables for time-based comparison
  const lastDismissedAt = moment(parseInt(lastDismissedAtTimestamp, 10));
  const thirtyMinutesLater = moment(lastDismissedAt).add(30, "m");
  const now = moment();

  return now.isBetween(lastDismissedAt, thirtyMinutesLater);
};

export const shouldPromptToBeginUnderwriting = (
  app: AppShape,
  hlhlContactRole: HLHLContactRole,
) => {
  const dismissedAppsMap = mapIfExists();

  const { id, underwriting_summary } = app;

  // If there is no UW summary yet, app isn't ready to begin underwriting
  if (!underwriting_summary) {
    return false;
  }

  const { loan_status } = underwriting_summary;

  // If loan_status is updated manually during the 30 minute window, remove ID from the localStorage map
  if (
    underwriting_summary &&
    loan_status === LoanStatus.InBorrowerUnderwriting &&
    dismissedAppsMap &&
    dismissedAppsMap.has(id)
  ) {
    removeTimestampForID(dismissedAppsMap, id);
  }

  return (
    hlhlContactRole === HLHLContactRole.LoanUnderwriter &&
    loan_status === LoanStatus.AuthPackageSigned &&
    !withinThirtyMinutes(id)
  );
};
